import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as adminApi from '../../api/admin';
import { generateTraceId, logAction } from '../../utils/trace';

// Async thunks
export const fetchTenantUsers = createAsyncThunk(
  'admin/fetchTenantUsers',
  async ({ tenantId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'admin/fetchTenantUsers/pending', meta: { traceId } }, {
        tenantId,
      });
      const data = await adminApi.getTenantUsers(tenantId, { traceId });
      logAction({ type: 'admin/fetchTenantUsers/fulfilled', meta: { traceId } }, {
        userCount: data.length,
      });
      return data;
    } catch (error) {
      logAction({ type: 'admin/fetchTenantUsers/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const addUserToTenant = createAsyncThunk(
  'admin/addUserToTenant',
  async ({ tenantId, email, role, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'admin/addUserToTenant/pending', meta: { traceId } }, {
        tenantId,
        email,
        role,
      });
      const data = await adminApi.addUserToTenant(tenantId, email, role, { traceId });
      logAction({ type: 'admin/addUserToTenant/fulfilled', meta: { traceId } }, {
        userId: data.user_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'admin/addUserToTenant/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const removeUserFromTenant = createAsyncThunk(
  'admin/removeUserFromTenant',
  async ({ tenantId, userId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'admin/removeUserFromTenant/pending', meta: { traceId } }, {
        tenantId,
        userId,
      });
      await adminApi.removeUserFromTenant(tenantId, userId, { traceId });
      logAction({ type: 'admin/removeUserFromTenant/fulfilled', meta: { traceId } }, {
        userId,
      });
      return userId;
    } catch (error) {
      logAction({ type: 'admin/removeUserFromTenant/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAllTenants = createAsyncThunk(
  'admin/fetchAllTenants',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'admin/fetchAllTenants/pending', meta: { traceId } });
      const data = await adminApi.getAllTenants({ traceId });
      logAction({ type: 'admin/fetchAllTenants/fulfilled', meta: { traceId } }, {
        tenantCount: data.length,
      });
      return data;
    } catch (error) {
      logAction({ type: 'admin/fetchAllTenants/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTenantsWithCounts = createAsyncThunk(
  'admin/fetchTenantsWithCounts',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'admin/fetchTenantsWithCounts/pending', meta: { traceId } });
      const data = await adminApi.getTenantsWithCounts({ traceId });
      logAction({ type: 'admin/fetchTenantsWithCounts/fulfilled', meta: { traceId } }, {
        tenantCount: data.length,
      });
      return data;
    } catch (error) {
      logAction({ type: 'admin/fetchTenantsWithCounts/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const mergeSelectedTenants = createAsyncThunk(
  'admin/mergeSelectedTenants',
  async ({ sourceTenantIds, targetTenantId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'admin/mergeSelectedTenants/pending', meta: { traceId } }, {
        sourceTenantIds,
        targetTenantId,
      });
      const data = await adminApi.mergeTenants(sourceTenantIds, targetTenantId, { traceId });
      logAction({ type: 'admin/mergeSelectedTenants/fulfilled', meta: { traceId } }, {
        mergedCount: sourceTenantIds.length,
      });
      return data;
    } catch (error) {
      logAction({ type: 'admin/mergeSelectedTenants/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    tenantUsers: [],
    allTenants: [],
    tenantsWithCounts: [],
    selectedTenantIds: [],
    isMerging: false,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    toggleTenantSelection: (state, action) => {
      const tenantId = action.payload;
      const index = state.selectedTenantIds.indexOf(tenantId);
      if (index >= 0) {
        state.selectedTenantIds.splice(index, 1);
      } else {
        state.selectedTenantIds.push(tenantId);
      }
    },
    clearTenantSelections: (state) => {
      state.selectedTenantIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchTenantUsers
      .addCase(fetchTenantUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTenantUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenantUsers = action.payload;
      })
      .addCase(fetchTenantUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // addUserToTenant
      .addCase(addUserToTenant.fulfilled, (state, action) => {
        state.tenantUsers.push(action.payload);
      })
      // removeUserFromTenant
      .addCase(removeUserFromTenant.fulfilled, (state, action) => {
        state.tenantUsers = state.tenantUsers.filter(
          u => u.user_id !== action.payload
        );
      })
      // fetchAllTenants
      .addCase(fetchAllTenants.fulfilled, (state, action) => {
        state.allTenants = action.payload;
      })
      // fetchTenantsWithCounts
      .addCase(fetchTenantsWithCounts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTenantsWithCounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tenantsWithCounts = action.payload;
      })
      .addCase(fetchTenantsWithCounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // mergeSelectedTenants
      .addCase(mergeSelectedTenants.pending, (state) => {
        state.isMerging = true;
        state.error = null;
      })
      .addCase(mergeSelectedTenants.fulfilled, (state, action) => {
        state.isMerging = false;
        state.selectedTenantIds = [];
      })
      .addCase(mergeSelectedTenants.rejected, (state, action) => {
        state.isMerging = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, toggleTenantSelection, clearTenantSelections } = adminSlice.actions;
export default adminSlice.reducer;
